import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Clock, LogOut, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { supabase } from '@/integrations/supabase/client';

export default function PendingOrgAccess() {
  const { user, signOut } = useAuth();
  const { organizations, loading, loadError, refreshOrganizations } = useOrganizationContext();
  const navigate = useNavigate();

  // Redireciona assim que uma org aparecer
  useEffect(() => {
    if (!loading && organizations.length > 0) {
      navigate('/chat', { replace: true });
    }
  }, [organizations.length, loading, navigate]);

  // Realtime: ouve INSERT em organization_members para este usuário
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('pending-access-membership')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'organization_members',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          refreshOrganizations();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, refreshOrganizations]);

  // Polling a cada 8s como fallback (caso Realtime falhe) — não insistir após erro de conexão
  useEffect(() => {
    if (!user || loadError) return;
    const interval = setInterval(() => {
      refreshOrganizations();
    }, 8000);
    return () => clearInterval(interval);
  }, [user?.id, loadError, refreshOrganizations]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-sm w-full rounded-xl border bg-card p-8 space-y-6 text-center shadow-sm">
        <div className="flex justify-center">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Clock className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-semibold tracking-tight">
            {loadError ? 'Problema ao carregar acesso' : 'Aguardando acesso'}
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {loadError === 'timeout'
              ? 'Não foi possível carregar suas organizações. Isso geralmente indica sessão expirada ou instabilidade de rede. Saia e entre novamente.'
              : loadError === 'error'
                ? 'Ocorreu um erro ao verificar suas organizações. Tente novamente ou faça login de novo.'
                : 'Sua conta ainda não foi vinculada a nenhuma organização. Aguarde o administrador configurar seu acesso.'}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Verificando automaticamente…</span>
        </div>

        <div className="space-y-2 pt-1">
          {!loadError && (
            <button
              onClick={() => refreshOrganizations()}
              className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <RefreshCw className="h-4 w-4" />
              Verificar agora
            </button>
          )}
          {loadError && (
            <button
              onClick={handleLogout}
              className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <LogOut className="h-4 w-4" />
              Sair e entrar novamente
            </button>
          )}
          {!loadError && (
            <button
              onClick={handleLogout}
              className="w-full h-9 rounded-md border text-sm text-muted-foreground flex items-center justify-center gap-2 hover:bg-muted transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sair da conta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
